import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit, signal, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ReportId } from '@core/models';
import { finalize } from 'rxjs/operators';
import {
  TenantConfiguration,
  TenantConfigurationService,
} from '@core/services/tenant-configuration.service';
import {
  LogoSelection,
  ReportOption,
  TenantConfigurationForm,
} from './configuracoes.models';

import { TenantIdentityTabComponent } from './tabs/tenant-identity-tab/tenant-identity-tab.component';
import { ReportSelectionTabComponent } from './tabs/report-selection-tab/report-selection-tab.component';

@Component({
  selector: 'app-configuracoes',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TenantIdentityTabComponent,
    ReportSelectionTabComponent,
  ],
  templateUrl: './configuracoes.component.html',
  styleUrl: './configuracoes.component.scss',
})
export class ConfiguracoesComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly messageService = inject(MessageService);
  private readonly tenantConfiguration = inject(TenantConfigurationService);

  // Tab Ativa
  readonly activeTab = signal<'identidade' | 'funcionalidade'>('identidade');

  private readonly reportOptionCatalog: ReportOption[] = [
    { id: 'efetivos', nome: 'Efetivos', descricao: 'Listagens e estado dos efetivos.', icon: 'fas fa-users' },
    { id: 'faltas', nome: 'Faltas', descricao: 'Assiduidade, faltas e atrasos.', icon: 'fas fa-clock' },
    { id: 'escalas', nome: 'Escalas e Turnos', descricao: 'Planeamento de escalas e turnos.', icon: 'fas fa-calendar-alt' },
    { id: 'ocorrencias', nome: 'Ocorrências', descricao: 'Ocorrências por gravidade e posto.', icon: 'fas fa-triangle-exclamation' },
    { id: 'rondas', nome: 'Rondas', descricao: 'Cumprimento e controlo de rondas.', icon: 'fas fa-route' },
    { id: 'financeiro', nome: 'Financeiro', descricao: 'Facturação e custos operacionais.', icon: 'fas fa-chart-line' },
    { id: 'postos', nome: 'Postos', descricao: 'Estado e cobertura dos postos.', icon: 'fas fa-map-marker-alt' },
    { id: 'equipamentos', nome: 'Equipamentos', descricao: 'Inventário e atribuições.', icon: 'fas fa-shield-alt' },
  ];

  reportOptions: ReportOption[] = [];

  readonly form: TenantConfigurationForm = this.fb.nonNullable.group({
    nome: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(120)]],
    nif: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(20), Validators.pattern(/^[A-Za-z0-9]+$/)]],
    enabledReportIds: this.fb.nonNullable.control<ReportId[]>([]),
  });

  // Sinal computado para obter a contagem de relatórios ativos no badge
  readonly enabledReportsCount = signal<number>(0);

  logoPreview: string | null = null;
  readonly loading = signal(true);
  readonly loadFailed = signal(false);
  readonly saving = signal(false);

  private savedConfiguration = this.tenantConfiguration.configuration();
  private selectedLogo: File | null = null;
  private logoChanged = false;

  ngOnInit(): void {
    this.loadConfiguration();
    
    // Atualiza a contagem sempre que a seleção de relatórios mudar
    this.form.controls.enabledReportIds.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((ids) => {
        this.enabledReportsCount.set(ids?.length || 0);
      });
  }

  setActiveTab(tab: 'identidade' | 'funcionalidade'): void {
    this.activeTab.set(tab);
  }

  loadConfiguration(): void {
    this.loading.set(true);
    this.loadFailed.set(false);

    this.tenantConfiguration
      .load()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        }),
      )
      .subscribe({
        next: (configuration) => this.applyConfiguration(configuration),
        error: () => {
          this.loadFailed.set(true);
        },
      });
  }

  onLogoChange(selection: LogoSelection): void {
    this.selectedLogo = selection.file;
    this.logoPreview = selection.previewUrl;
    this.logoChanged = true;
    this.form.markAsDirty();
  }

  restore(): void {
    this.applyConfiguration(this.savedConfiguration);
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.showError('Preencha correctamente os dados obrigatórios.');
      return;
    }

    this.saving.set(true);
    this.tenantConfiguration
      .save({
        configuration: {
          ...this.savedConfiguration,
          ...this.form.getRawValue(),
          logoDataUrl: this.logoPreview,
        },
        logoFile: this.selectedLogo,
        logoChanged: this.logoChanged,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.saving.set(false);
        }),
      )
      .subscribe({
        next: (configuration) => {
          this.applyConfiguration(configuration);
          this.messageService.add({
            severity: 'success',
            summary: 'Configurações guardadas',
            detail: 'Os dados do tenant foram actualizados.',
          });
        },
      });
  }

  private applyConfiguration(configuration: TenantConfiguration): void {
    this.savedConfiguration = {
      ...configuration,
      reportConfigurations: configuration.reportConfigurations.map((report) => ({
        ...report,
      })),
      enabledReportIds: [...configuration.enabledReportIds],
    };
    const optionsById = new Map(
      this.reportOptionCatalog.map((option) => [option.id, option]),
    );
    this.reportOptions = configuration.reportConfigurations
      .map((report) => {
        const option = optionsById.get(report.id);
        return option ? { ...option, nome: report.name } : null;
      })
      .filter((option): option is ReportOption => option !== null);
    
    this.form.reset({
      nome: configuration.nome,
      nif: configuration.nif,
      enabledReportIds: [...configuration.enabledReportIds],
    });
    
    this.enabledReportsCount.set(configuration.enabledReportIds.length);
    this.logoPreview = configuration.logoDataUrl;
    this.selectedLogo = null;
    this.logoChanged = false;
  }

  showError(detail: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Não foi possível guardar',
      detail,
    });
  }
}